import React, { useState, useEffect } from 'react';
import { Wifi, WifiOff, AlertTriangle } from 'lucide-react';

interface ApiHealth {
  status: 'healthy' | 'slow' | 'down';
  lastCheck: number;
  responseTime: number;
  consecutiveFailures: number;
}

const ApiHealthIndicator: React.FC = () => {
  const [health, setHealth] = useState<ApiHealth>(() => {
    const stored = localStorage.getItem('api-health');
    return stored ? JSON.parse(stored) : {
      status: 'healthy',
      lastCheck: 0,
      responseTime: 0,
      consecutiveFailures: 0
    };
  });

  useEffect(() => {
    // Check API health every 2 minutes
    const checkHealth = async () => {
      const startTime = Date.now();
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);
        
        const response = await fetch('/api/health', { 
          method: 'GET',
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        const endTime = Date.now();
        const responseTime = endTime - startTime;
        
        const newHealth: ApiHealth = {
          status: response.ok ? (responseTime > 2000 ? 'slow' : 'healthy') : 'down',
          lastCheck: Date.now(),
          responseTime,
          consecutiveFailures: response.ok ? 0 : health.consecutiveFailures + 1
        };
        
        setHealth(newHealth);
        localStorage.setItem('api-health', JSON.stringify(newHealth));
      } catch (error) {
        const newHealth: ApiHealth = {
          status: 'down',
          lastCheck: Date.now(),
          responseTime: 0,
          consecutiveFailures: health.consecutiveFailures + 1
        };
        
        setHealth(newHealth);
        localStorage.setItem('api-health', JSON.stringify(newHealth));
      }
    };

    checkHealth();
    const interval = setInterval(checkHealth, 120000); // Check every 2 minutes
    
    return () => clearInterval(interval);
  }, []);

  const getStatusColor = () => {
    switch (health.status) {
      case 'healthy': return 'text-green-600';
      case 'slow': return 'text-yellow-600';
      case 'down': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const getStatusIcon = () => {
    switch (health.status) {
      case 'healthy': return <Wifi className="h-4 w-4" />;
      case 'slow': return <AlertTriangle className="h-4 w-4" />;
      case 'down': return <WifiOff className="h-4 w-4" />;
      default: return <Wifi className="h-4 w-4" />;
    }
  };

  const getStatusText = () => {
    switch (health.status) {
      case 'healthy': return 'API Healthy';
      case 'slow': return `API Slow (${health.responseTime}ms)`;
      case 'down': return `API Down (${health.consecutiveFailures} failures)`;
      default: return 'API Status Unknown';
    }
  };

  return (
    <div className={`flex items-center space-x-2 px-3 py-2 rounded-lg shadow-sm border ${
      health.status === 'healthy' ? 'bg-green-50 border-green-200' :
      health.status === 'slow' ? 'bg-yellow-50 border-yellow-200' :
      'bg-red-50 border-red-200'
    }`}>
      <span className={getStatusColor()}>
        {getStatusIcon()}
      </span>
      <span className={`text-sm font-medium ${getStatusColor()}`}>
        {getStatusText()}
      </span>
      {health.lastCheck > 0 && (
        <span className="text-xs text-gray-500">
          {new Date(health.lastCheck).toLocaleTimeString()}
        </span>
      )}
    </div>
  );
};

export default ApiHealthIndicator;